import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  EmptyState,
  FilterChips,
  Input,
  Modal,
  StatusDot,
  Table,
  Text,
} from "@teals/ui-web";
import type { Column } from "@teals/ui-web";
import {
  useDeleteMcpIntegrationMutation,
  useGetMcpIntegrationQuery,
  useUpdateMcpAgentAssociationsMutation,
  useUpdateMcpIntegrationMutation,
  useVerifyMcpIntegrationMutation,
  mcpHost,
  type McpDiscoveredTool,
  type McpIntegration,
  type VerifyMcpIntegrationRequest,
} from "../../store/api/mcpIntegrationsApi";
import { useGetAgentsQuery } from "../../store/api/agentsApi";
import { useAuth } from "../../hooks/useAuth";
import { formatRelativeTime } from "../DataSources/relativeTime";
import {
  AssociationItem,
  AssociationName,
  CodeBlock,
  CopyButton,
  DetailField,
  DetailFieldLabel,
  DetailFieldValue,
  DetailSection,
  DetailSectionTitle,
} from "../DataSources/styles";
import { PageWrapper } from "../DataSources/styles";
import {
  ConnectionStrip,
  Crumb,
  InstanceHeader,
  InstanceActions,
  InstanceTitleRow,
  StripItem,
} from "./styles";
import { INTEGRATIONS_HUB } from "./paths";

function snippetFor(mcp: McpIntegration): string {
  const param = mcp.payloadParameter || "x-cv-mcp-token";
  if ((mcp.callerTokenIn || "header") === "body") {
    return `{\n  "parameters": { "${param}": "<user-scoped token>" }\n}`;
  }
  return `POST /v1/chat\nx-api-key: lf_ak_…\nx-secret-key: lf_sk_…\n${param}: <user-scoped token>`;
}

const CustomMcpInstance: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organizationId || "";
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [trialToken, setTrialToken] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [singleToken, setSingleToken] = useState("");
  const [useStored, setUseStored] = useState(false);
  const [addAgentId, setAddAgentId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: mcp, isLoading, error: loadError } = useGetMcpIntegrationQuery(
    id ?? "",
    { skip: !id },
  );
  const { data: agents = [] } = useGetAgentsQuery({ orgId }, { skip: !orgId });
  const [deleteMcp] = useDeleteMcpIntegrationMutation();
  const [updateMcp] = useUpdateMcpIntegrationMutation();
  const [verifyMcp, { isLoading: verifying }] = useVerifyMcpIntegrationMutation();
  const [updateAssociations] = useUpdateMcpAgentAssociationsMutation();

  const associatedAgents = useMemo(() => {
    const ids = new Set(mcp?.agentIds || []);
    return agents.filter((a) => ids.has(a.id) || ids.has(a.agentName));
  }, [mcp?.agentIds, agents]);

  const availableAgents = useMemo(() => {
    const ids = new Set(mcp?.agentIds || []);
    return agents.filter(
      (a) => !a.readOnly && !ids.has(a.id) && !ids.has(a.agentName),
    );
  }, [mcp?.agentIds, agents]);

  const toolColumns: Column<McpDiscoveredTool>[] = useMemo(
    () => [
      { key: "name", header: "Tool" },
      {
        key: "description",
        header: "Description",
        render: (val: string) => val || "—",
      },
    ],
    [],
  );

  const handleDelete = async () => {
    if (!mcp) return;
    try {
      await deleteMcp({ id: mcp.mcpId, confirm: true }).unwrap();
      navigate(INTEGRATIONS_HUB);
    } catch (e) {
      setError("Delete failed");
    }
  };

  const handleVerify = async () => {
    if (!mcp) return;
    setError(null);
    try {
      const body: VerifyMcpIntegrationRequest =
        mcp.authMode === "caller_token"
          ? { trial_token: trialToken }
          : useStored
            ? { use_stored: true }
            : {
                trial_credentials:
                  mcp.credentialTemplate === "key_pair"
                    ? { access_key: accessKey, secret_key: secretKey }
                    : { token: singleToken },
              };
      const result = await verifyMcp({ id: mcp.mcpId, body }).unwrap();
      if (!result.ok) {
        setError(result.message || result.error || "Verify failed");
        return;
      }
      setVerifyOpen(false);
      setTrialToken("");
      setAccessKey("");
      setSecretKey("");
      setSingleToken("");
    } catch {
      setError("Verify failed");
    }
  };

  const handleRotate = async () => {
    if (!mcp) return;
    setError(null);
    try {
      const credentials: Record<string, string> =
        mcp.credentialTemplate === "key_pair"
          ? { access_key: accessKey, secret_key: secretKey }
          : { token: singleToken };
      await updateMcp({
        id: mcp.mcpId,
        body: { rotate: true, credentials },
      }).unwrap();
      setRotateOpen(false);
      setAccessKey("");
      setSecretKey("");
      setSingleToken("");
    } catch {
      setError("Rotate failed");
    }
  };

  if (!id) {
    return (
      <PageWrapper>
        <EmptyState title="Missing Custom MCP id." />
      </PageWrapper>
    );
  }

  if (isLoading) {
    return (
      <PageWrapper>
        <EmptyState title="Loading Custom MCP…" />
      </PageWrapper>
    );
  }

  if (loadError || !mcp) {
    return (
      <PageWrapper>
        <Crumb>
          <Link to={INTEGRATIONS_HUB}>Integrations</Link>
          {" → Custom MCP"}
        </Crumb>
        <EmptyState
          title="Custom MCP not found."
          action={
            <Button variant="outline" onClick={() => navigate(INTEGRATIONS_HUB)}>
              Back to Integrations
            </Button>
          }
        />
      </PageWrapper>
    );
  }

  const authLabel =
    mcp.authMode === "stored_credentials" ? "Stored credentials" : "Caller token";
  const verified = mcp.lastVerifiedAt
    ? formatRelativeTime(mcp.lastVerifiedAt).replace("synced", "verified")
    : "Never verified";

  return (
    <PageWrapper>
      <Crumb>
        <Link to={INTEGRATIONS_HUB}>Integrations</Link>
        {` → ${mcp.name}`}
      </Crumb>
      <InstanceHeader>
        <div>
          <InstanceTitleRow>
            <Text variant="h2">{mcp.name}</Text>
            <Badge variant="info" size="sm">
              Custom MCP
            </Badge>
            <StatusDot status={mcp.status === "active" ? "active" : "draft"} />
          </InstanceTitleRow>
        </div>
        <InstanceActions>
          <Button variant="outline" onClick={() => setVerifyOpen(true)}>
            Verify
          </Button>
          {mcp.authMode === "stored_credentials" && (
            <Button variant="outline" onClick={() => setRotateOpen(true)}>
              Rotate
            </Button>
          )}
          <Button variant="danger" onClick={() => setDeleteConfirm(true)}>
            Delete
          </Button>
        </InstanceActions>
      </InstanceHeader>

      <ConnectionStrip>
        <StripItem>
          <label>Public id</label>
          <span>{mcp.mcpId}</span>
        </StripItem>
        <StripItem>
          <label>Host</label>
          <span>{mcpHost(mcp.serverUrl) || "—"}</span>
        </StripItem>
        <StripItem>
          <label>Auth</label>
          <span>{authLabel}</span>
        </StripItem>
        <StripItem>
          <label>Last verified</label>
          <span>{verified}</span>
        </StripItem>
        <StripItem>
          <label>Organisation</label>
          <span>{orgId}</span>
        </StripItem>
      </ConnectionStrip>

      {error && (
        <Text variant="body2" style={{ color: "var(--danger, #b91c1c)" }}>
          {error}
        </Text>
      )}

      <DetailSection>
        <DetailSectionTitle>Connection</DetailSectionTitle>
        <DetailField>
          <DetailFieldLabel>URL</DetailFieldLabel>
          <DetailFieldValue>{mcp.serverUrl}</DetailFieldValue>
        </DetailField>
        {mcp.authMode === "caller_token" && (
          <>
            <DetailField>
              <DetailFieldLabel>Send as</DetailFieldLabel>
              <FilterChips
                items={[
                  { id: "header", label: "HTTP header" },
                  { id: "body", label: "Request body" },
                ]}
                activeId={mcp.callerTokenIn || "header"}
                onChange={async (id) => {
                  await updateMcp({
                    id: mcp.mcpId,
                    body: { caller_token_in: id as "header" | "body" },
                  }).unwrap();
                }}
              />
            </DetailField>
            <DetailField>
              <DetailFieldLabel>Parameter</DetailFieldLabel>
              <DetailFieldValue>{mcp.payloadParameter}</DetailFieldValue>
            </DetailField>
            <CodeBlock>{snippetFor(mcp)}</CodeBlock>
            <CopyButton
              type="button"
              onClick={() => navigator.clipboard?.writeText(snippetFor(mcp))}
            >
              Copy snippet
            </CopyButton>
          </>
        )}
        {mcp.authMode === "stored_credentials" && (
          <DetailField>
            <DetailFieldLabel>Stored fields</DetailFieldLabel>
            <DetailFieldValue>
              {(mcp.authFields || [])
                .map((f) => `${f.name}${f.configured ? " (configured)" : ""}`)
                .join(", ") || "None"}
            </DetailFieldValue>
          </DetailField>
        )}
      </DetailSection>

      <DetailSection>
        <DetailSectionTitle>Discovered tools</DetailSectionTitle>
        {(mcp.discoveredTools || []).length === 0 ? (
          <EmptyState title="Verify to discover tools." />
        ) : (
          <Table
            columns={toolColumns}
            data={mcp.discoveredTools || []}
            rowKey={(row) => row.name}
          />
        )}
      </DetailSection>

      <DetailSection>
        <DetailSectionTitle>Agent associations</DetailSectionTitle>
        {associatedAgents.length === 0 ? (
          <EmptyState title="No agents associated yet." />
        ) : (
          associatedAgents.map((agent) => (
            <AssociationItem key={agent.id}>
              <AssociationName>
                <Link to={`/agents/${agent.id}?tab=configure`}>{agent.name}</Link>
              </AssociationName>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  const next = (mcp.agentIds || []).filter(
                    (aid) => aid !== agent.id && aid !== agent.agentName,
                  );
                  await updateAssociations({ id: mcp.mcpId, agentIds: next }).unwrap();
                }}
              >
                Remove
              </Button>
            </AssociationItem>
          ))
        )}
        {availableAgents.length > 0 && (
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
            <select
              aria-label="Add agent"
              value={addAgentId}
              onChange={(e) => setAddAgentId(e.target.value)}
            >
              <option value="">Associate an agent…</option>
              {availableAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              disabled={!addAgentId}
              onClick={async () => {
                await updateAssociations({
                  id: mcp.mcpId,
                  agentIds: [...(mcp.agentIds || []), addAgentId],
                }).unwrap();
                setAddAgentId("");
              }}
            >
              Add
            </Button>
          </div>
        )}
      </DetailSection>

      <Modal
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        title="Delete Custom MCP?"
      >
        <Text variant="body1">
          This will disconnect {associatedAgents.length} associated agent
          {associatedAgents.length === 1 ? "" : "s"}. Soft-delete cannot be undone
          from this page.
        </Text>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        title="Verify Custom MCP"
      >
        {mcp.authMode === "caller_token" ? (
          <Input
            label="Trial token"
            type="password"
            value={trialToken}
            onChange={(e) => setTrialToken(e.target.value)}
            autoComplete="off"
          />
        ) : (
          <>
            <Button
              variant={useStored ? "primary" : "outline"}
              size="sm"
              onClick={() => setUseStored(true)}
            >
              Try with configured keys
            </Button>
            {!useStored && mcp.credentialTemplate === "key_pair" && (
              <>
                <Input
                  label="Access key"
                  type="password"
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                  autoComplete="off"
                />
                <Input
                  label="Secret key"
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  autoComplete="off"
                />
              </>
            )}
            {!useStored && mcp.credentialTemplate !== "key_pair" && (
              <Input
                label="Token"
                type="password"
                value={singleToken}
                onChange={(e) => setSingleToken(e.target.value)}
                autoComplete="off"
              />
            )}
          </>
        )}
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={() => setVerifyOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={verifying} onClick={handleVerify}>
            Try
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={rotateOpen}
        onClose={() => setRotateOpen(false)}
        title="Rotate stored credentials"
      >
        {mcp.credentialTemplate === "key_pair" ? (
          <>
            <Input
              label="Access key"
              type="password"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              autoComplete="off"
            />
            <Input
              label="Secret key"
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              autoComplete="off"
            />
          </>
        ) : (
          <Input
            label="Token"
            type="password"
            value={singleToken}
            onChange={(e) => setSingleToken(e.target.value)}
            autoComplete="off"
          />
        )}
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={() => setRotateOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRotate}>
            Rotate
          </Button>
        </div>
      </Modal>
    </PageWrapper>
  );
};

export default CustomMcpInstance;
